import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Plus, Edit, Trash2, TrendingDown, Calendar, DollarSign, X, Pause, Play, Archive, Undo2 } from 'lucide-react';
import { expensesApi, companiesApi } from '../services/api';
import { useTableSort } from '../hooks/useTableSort';
import { useToast } from '../hooks/use-toast';

const ExpenseManager = ({ onClose }) => {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [viewTab, setViewTab] = useState('aktiv'); // aktiv | pausiert | archiv
  // Filter wie bei den Einnahmen: Suche + Brand + Kategorie + Status
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [intervalFilter, setIntervalFilter] = useState('all');
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    companyId: '',
    title: '',
    description: '',
    amount: '',
    category: 'operating',
    status: 'active',
    isRecurring: false,
    recurringInterval: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: ''
  });

  const categories = {
    operating: 'Betriebskosten',
    marketing: 'Marketing',
    personnel: 'Personal',
    equipment: 'Ausrüstung',
    rent: 'Miete',
    utilities: 'Nebenkosten',
    insurance: 'Versicherung',
    software: 'Software/Lizenzen',
    other: 'Sonstiges'
  };

  const statusLabels = {
    planned: 'Geplant',
    active: 'Aktiv',
    paused: 'Pausiert',
    cancelled: 'Storniert',
    archived: 'Archiviert'
  };

  const intervals = {
    weekly: 'Wöchentlich',
    monthly: 'Monatlich',
    quarterly: 'Vierteljährlich',
    semiannual: 'Halbjährlich',
    yearly: 'Jährlich'
  };

  useEffect(() => {
    loadExpenses();
    loadSummary();
    companiesApi.getAll().then(r => setCompanies(r.data || [])).catch(() => {});
  }, []);

  const brandName = (companyId) => {
    const c = companies.find(x => x.id === companyId);
    return c ? c.name : (companyId === 'default_company' ? 'Übergreifend (alt)' : '—');
  };

  // Nächste Abrechnung = erstes Fälligkeitsdatum >= heute (Startdatum + n × Zyklus)
  const computeNextBilling = (startDate, interval) => {
    if (!startDate || !interval) return null;
    const stepMonths = { monthly: 1, quarterly: 3, semiannual: 6, yearly: 12 };
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let d = new Date(startDate);
    if (interval === 'weekly') {
      while (d < today) d.setDate(d.getDate() + 7);
    } else {
      const m = stepMonths[interval] || 1;
      while (d < today) d.setMonth(d.getMonth() + m);
    }
    return d;
  };

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const response = await expensesApi.getAll();
      setExpenses(response.data || []);
    } catch (error) {
      console.error('Error loading expenses:', error);
      toast({
        title: "Fehler",
        description: "Fixkosten konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await expensesApi.getSummary();
      setSummary(response.data || {});
    } catch (error) {
      console.error('Error loading expense summary:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.companyId) {
      toast({ title: "Hinweis", description: "Bitte eine Brand/Firma auswählen.", variant: "destructive" });
      return;
    }
    if (!formData.title || !formData.amount || !formData.startDate) {
      toast({
        title: "Hinweis",
        description: "Bitte füllen Sie alle Pflichtfelder aus.",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      
      const expenseData = {
        ...formData,
        amount: parseFloat(formData.amount),
        companyId: formData.companyId,
        recurringInterval: formData.isRecurring ? formData.recurringInterval : null,
        endDate: formData.endDate || null
      };

      if (editingExpense) {
        await expensesApi.update(editingExpense.id, expenseData);
        toast({
          title: "Erfolg",
          description: "Ausgabe erfolgreich aktualisiert.",
        });
      } else {
        await expensesApi.create(expenseData);
        toast({
          title: "Erfolg",
          description: "Ausgabe erfolgreich angelegt.",
        });
      }
      
      resetForm();
      await loadExpenses();
      await loadSummary();
    } catch (error) {
      console.error('Error saving expense:', error);
      toast({
        title: "Fehler",
        description: error?.response?.data?.detail || "Ausgabe konnte nicht gespeichert werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      companyId: expense.companyId || '',
      title: expense.title,
      description: expense.description || '',
      amount: expense.amount.toString(),
      category: expense.category,
      status: expense.status,
      isRecurring: expense.isRecurring,
      recurringInterval: expense.recurringInterval || '',
      startDate: expense.startDate,
      endDate: expense.endDate || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (expense) => {
    if (!window.confirm('Möchten Sie diese Fixkosten-Position wirklich löschen?')) {
      return;
    }

    try {
      setLoading(true);
      await expensesApi.delete(expense.id, expense.companyId);
      toast({
        title: "Erfolg",
        description: "Ausgabe wurde gelöscht.",
      });
      await loadExpenses();
      await loadSummary();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast({
        title: "Fehler",
        description: "Ausgabe konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Schnell-Aktionen: Status direkt aus der Zeile ändern (ohne Formular)
  const quickStatus = async (expense, newStatus) => {
    try {
      setLoading(true);
      await expensesApi.update(expense.id, { ...expense, status: newStatus, companyId: expense.companyId });
      toast({ title: "Erledigt", description: `„${expense.title}" → ${statusLabels[newStatus]}.` });
      await loadExpenses();
      await loadSummary();
    } catch (error) {
      toast({ title: "Fehler", description: "Status konnte nicht geändert werden.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const tabOf = (e) =>
    e.status === 'paused' ? 'pausiert'
      : (e.status === 'archived' || e.status === 'cancelled') ? 'archiv'
      : 'aktiv';

  const q = search.trim().toLowerCase();
  const filteredExpenses = expenses
    .filter(e => {
      if (tabOf(e) !== viewTab) return false;
      if (brandFilter !== 'all' && e.companyId !== brandFilter) return false;
      if (categoryFilter !== 'all' && e.category !== categoryFilter) return false;
      if (statusFilter !== 'all' && e.status !== statusFilter) return false;
      if (intervalFilter !== 'all' && e.recurringInterval !== intervalFilter) return false;
      if (q && !(`${e.title} ${e.description || ''} ${categories[e.category] || e.category || ''} ${brandName(e.companyId)}`.toLowerCase().includes(q))) return false;
      return true;
    })
    .map(e => ({ ...e, _brand: brandName(e.companyId) }));
  const { sorted: visibleExpenses, requestSort, indicator } = useTableSort(filteredExpenses, 'amount');

  // KPI-Kacheln folgen der Filterung: nur die aktuell angezeigten Zeilen zählen.
  // WICHTIG: Die Kacheln zeigen die GESAMTBELASTUNG, umgerechnet auf den
  // jeweiligen Zeitraum — nicht die Summe der Posten mit diesem Zyklus.
  // Ein Jahresposten von 1.200 € zaehlt also mit 100 € in "pro Monat".
  const MONTH_FACTOR = {
    weekly: 52 / 12,
    monthly: 1,
    quarterly: 1 / 3,
    semiannual: 1 / 6,
    yearly: 1 / 12,
  };
  const monthlyEquivalent = filteredExpenses.reduce((s, e) => {
    const f = MONTH_FACTOR[e.recurringInterval];
    if (!f) return s;                 // ohne Zyklus = einmalig, keine laufende Last
    return s + (Number(e.amount) || 0) * f;
  }, 0);
  const filteredSummary = {
    monthly: monthlyEquivalent,
    quarterly: monthlyEquivalent * 3,
    yearly: monthlyEquivalent * 12,
    count: filteredExpenses.length,
  };

  const sortableHead = (label, key, extra = '') => (
    <TableHead
      className={`cursor-pointer select-none hover:bg-slate-50 ${extra}`}
      onClick={() => requestSort(key)}
    >
      {label}{indicator(key)}
    </TableHead>
  );

  const resetForm = () => {
    setFormData({
      companyId: '',
      title: '',
      description: '',
      amount: '',
      category: 'operating',
      status: 'active',
      isRecurring: false,
      recurringInterval: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: ''
    });
    setEditingExpense(null);
    setShowForm(false);
  };

  return (
    <div className="space-y-6 p-6">
      {/* Titel liefert die Calculator-Seite — hier nur die Aktion */}
      <div className="flex justify-end">
        <Button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white">
          <Plus className="w-4 h-4" />
          Neue Fixkosten-Position
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">Gesamt / Monat</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {filteredSummary.monthly.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
            </div>
            <div className="text-xs text-slate-400 mt-1">alle Zyklen auf einen Monat umgerechnet</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">Gesamt / Quartal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {filteredSummary.quarterly.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
            </div>
            <div className="text-xs text-slate-400 mt-1">alle Zyklen auf ein Quartal umgerechnet</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">Gesamt / Jahr</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {filteredSummary.yearly.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
            </div>
            <div className="text-xs text-slate-400 mt-1">alle Zyklen auf ein Jahr umgerechnet</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">Angezeigte Positionen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-800">
              {filteredSummary.count}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expenses Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle>Fixkosten (netto)</CardTitle>
            <div className="flex flex-wrap gap-2 items-center">
              <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                {[['aktiv', 'Aktiv'], ['pausiert', 'Pausiert'], ['archiv', 'Archiv']].map(([key, label]) => (
                  <button key={key} type="button" onClick={() => setViewTab(key)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${viewTab === key ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
                    {label} ({expenses.filter(e => tabOf(e) === key).length})
                  </button>
                ))}
              </div>
              <Input
                placeholder="Suchen (Titel, Brand, Kategorie)…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-56"
              />
              <Select value={brandFilter} onValueChange={setBrandFilter}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Brands</SelectItem>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Kategorien</SelectItem>
                  {Object.entries(categories).map(([v, l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={intervalFilter} onValueChange={setIntervalFilter}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Zyklen</SelectItem>
                  {Object.entries(intervals).map(([v, l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Status</SelectItem>
                  {Object.entries(statusLabels).map(([v, l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && expenses.length === 0 ? (
            <div className="text-center py-8 text-slate-500">Lade Fixkosten...</div>
          ) : visibleExpenses.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <TrendingDown className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>{expenses.length === 0 ? 'Noch keine Fixkosten angelegt' : 'Keine Treffer für die aktuelle Filterung'}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {sortableHead('Titel', 'title')}
                  {sortableHead('Brand', '_brand')}
                  {sortableHead('Kategorie', 'category')}
                  {sortableHead('Betrag (netto)', 'amount')}
                  {sortableHead('Zyklus', 'recurringInterval')}
                  {sortableHead('Nächste Abrechnung', 'startDate')}
                  {sortableHead('Status', 'status')}
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleExpenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{expense.title}</div>
                        {expense.description && (
                          <div className="text-xs text-slate-500">{expense.description}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-slate-100 text-slate-700 border-slate-300">{brandName(expense.companyId)}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{categories[expense.category]}</Badge>
                    </TableCell>
                    <TableCell className="font-semibold text-red-600">
                      {expense.amount.toLocaleString('de-DE', { minimumFractionDigits: 2 })} € <span className="text-xs font-normal text-slate-400">netto</span>
                    </TableCell>
                    <TableCell>
                      {expense.isRecurring ? (
                        <Badge className="bg-orange-100 text-orange-800 border-orange-300">
                          {intervals[expense.recurringInterval]}
                        </Badge>
                      ) : (
                        <span className="text-xs text-slate-500">Einmalig</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {expense.isRecurring && expense.recurringInterval ? (
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="w-3 h-3 text-slate-500" />
                          {(computeNextBilling(expense.startDate, expense.recurringInterval) || new Date(expense.startDate)).toLocaleDateString('de-DE')}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        className={
                          expense.status === 'active' ? 'bg-green-100 text-green-800 border-green-300' :
                          expense.status === 'planned' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                          expense.status === 'paused' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                          expense.status === 'archived' ? 'bg-slate-200 text-slate-500 border-slate-300' :
                          'bg-slate-100 text-slate-800 border-slate-300'
                        }
                      >
                        {statusLabels[expense.status]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-1">
                        {expense.status === 'active' ? (
                          <Button size="sm" variant="ghost" title="Pausieren"
                            onClick={() => quickStatus(expense, 'paused')}
                            className="text-slate-500 hover:text-yellow-600">
                            <Pause className="h-4 w-4" />
                          </Button>
                        ) : (expense.status === 'paused' || expense.status === 'planned') ? (
                          <Button size="sm" variant="ghost" title="Fortsetzen"
                            onClick={() => quickStatus(expense, 'active')}
                            className="text-slate-500 hover:text-emerald-600">
                            <Play className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" title="Reaktivieren"
                            onClick={() => quickStatus(expense, 'active')}
                            className="text-slate-500 hover:text-emerald-600">
                            <Undo2 className="h-4 w-4" />
                          </Button>
                        )}
                        {expense.status !== 'archived' && (
                          <Button size="sm" variant="ghost" title="Archivieren"
                            onClick={() => quickStatus(expense, 'archived')}
                            className="text-slate-500 hover:text-slate-700">
                            <Archive className="h-4 w-4" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" title="Bearbeiten" onClick={() => handleEdit(expense)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Löschen"
                          onClick={() => handleDelete(expense)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingExpense ? 'Fixkosten-Position bearbeiten' : 'Neue Fixkosten-Position anlegen'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="companyId">Brand / Firma *</Label>
                <Select value={formData.companyId} onValueChange={(value) => setFormData({ ...formData, companyId: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Bei welcher Firma fallen die Kosten an?" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="title">Titel *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="z.B. Büromiete"
                  required
                />
              </div>

              <div>
                <Label htmlFor="amount">Betrag (netto) *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0,00"
                  required
                />
              </div>

              <div>
                <Label htmlFor="category">Kategorie</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categories).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(statusLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="startDate">Startdatum *</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="endDate">Enddatum (optional)</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">Beschreibung</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Zusätzliche Informationen..."
                rows={2}
              />
            </div>

            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded">
              <input
                type="checkbox"
                id="isRecurring"
                checked={formData.isRecurring}
                onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="isRecurring" className="cursor-pointer">
                Wiederkehrend (Abrechnungszyklus)
              </Label>
            </div>

            {formData.isRecurring && (
              <div>
                <Label htmlFor="recurringInterval">Abrechnungszyklus</Label>
                <Select 
                  value={formData.recurringInterval} 
                  onValueChange={(value) => setFormData({ ...formData, recurringInterval: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Wählen Sie einen Turnus" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(intervals).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.recurringInterval && formData.startDate && (
                  <p className="text-sm text-slate-600 mt-2">
                    📅 Nächste Abrechnung: <strong>
                      {computeNextBilling(formData.startDate, formData.recurringInterval)?.toLocaleDateString('de-DE')}
                    </strong> <span className="text-slate-400">(automatisch aus Startdatum + Zyklus)</span>
                  </p>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={resetForm}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Speichere...' : (editingExpense ? 'Aktualisieren' : 'Anlegen')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpenseManager;
